// === MUI ICONS ===
import {
    Business,
    Chat,
    Group,
    Groups,
    HelpOutline,
    // Core Navigation
    Home,
    // General
    Info,
    Person,
    Settings,
} from '@mui/icons-material';
// === LUCIDE ICONS ===
import {
    Briefcase, // Projects
    Building2, // Company/Customers
    Calendar1, // Published Date
    CalendarCheck, // End Date
    CalendarClock, // Availability/Deadline
    CalendarCog, // Forecast
    CircleStar, // Competences
    ClipboardList, // List Overview
    ClipboardPen, // Edit Opportunity
    Contact, // About Me
    DollarSign, // Money
    // === OPPORTUNITY FIELDS ===
    ExternalLink, // External ID
    Eye, // Preview
    FileBadge, // Certificates
    Files, // Attachments
    Flag, // Start Date
    Gauge, // Level indicator
    Github, // GitHub
    Globe, // Website
    Goal, // Project End
    // === ENTITY TYPES ===
    HandMetal, // Skills
    Layers2, // Categories
    LayoutDashboard, // Overview
    Linkedin, // LinkedIn
    // === PROFILE SECTIONS ===
    Mail, // Contact
    MapPin, // Location
    Percent, // Utilization
    Rocket, // Missions
    Settings2, // Level change/edit
    Share2, // Share
    ShieldUser, // Roles
    Slack, // Slack
    // === CONTACT INFO ===
    Smartphone, // Phone
    Sparkles, // SuperPowers
    SquareLibrary, // Catalog
    Target, // Goals
    // === NAVIGATION ===
    TrendingUp, // Development
    // === ACTIONS ===
    UserPen, // Edit Profile
    Users, // Users/Team
    UsersRound, // Teams
    WandSparkles, // AI/Analysis
    Wifi, // Remote
} from 'lucide-react';
import React from 'react';

export enum EIconCategory {
    // === CORE ENTITY TYPES ===
    SKILLS = 'skills',
    COMPETENCES = 'competences',
    CERTIFICATES = 'certificates',
    ROLES = 'roles',

    // === MAIN NAVIGATION ===
    DASHBOARD = 'dashboard',
    PROFILE = 'profile',
    EMPLOYEES = 'employees',
    DEPARTMENTS = 'departments',
    TEAMS = 'teams',
    SETTINGS = 'settings',
    T4LO_TALKS = 't4lo_talks',
    DEVELOPMENT = 'development',

    // === OVERVIEW & LISTS ===
    OVERVIEW = 'overview',
    LIST_OVERVIEW = 'list_overview',
    GENERAL = 'general',
    INFO = 'info',

    // === PROFILE ACTIONS ===
    EDIT = 'edit',
    SHARE = 'share',
    PROFILE_PREVIEW = 'profile_preview',
    CV_ANALYSE = 'cv_analyse',
    DEVELOP = 'develop',
    DEVELOPMENT_GOALS = 'development_goals',

    // === OPPORTUNITY ACTIONS ===
    EDIT_OPPORTUNITY = 'edit_opportunity',

    // === PROFILE SECTIONS ===
    CONTACT = 'contact',
    PROJECTS = 'projects',
    SUPERPOWERS = 'superpowers',
    ABOUTME = 'aboutme',
    MISSIONS = 'missions',
    AVAILABILITY = 'availability',
    TAB_FORECAST = 'tab_forecast',

    // === CONTACT INFORMATION ===
    PHONE = 'phone',
    LOCATION = 'location',
    GITHUB = 'github',
    SLACK = 'slack',
    MS_TEAMS = 'ms_teams',
    LINKEDIN = 'linkedin',
    WEBSITE = 'website',

    // === OPPORTUNITY FIELDS ===
    EXTERNAL_ID = 'external_id',
    COMPANY = 'company',
    CUSTOMERS = 'customers',
    UTILIZATION = 'utilization',
    REMOTE = 'remote',
    MONEY = 'money',
    START_DATE = 'start_date',
    END_DATE = 'end_date',
    USERS = 'users',
    ATTACHMENTS = 'attachments',

    // === DATE FIELDS ===
    PUBLISHED_DATE = 'published_date',
    PROJECT_END = 'project_end',
    DEADLINE = 'deadline',

    // === LEVEL INDICATOR ===
    LEVEL = 'level',
    LEVELCHANGE = 'levelchange',

    // === CATEGORIES ===
    CATEGORIES = 'categories',
    CATALOG = 'catalog',

    // === MESSAGING & COMMUNICATION ===
    MESSAGES = 'messages',
    CALENDAR = 'calendar',

    // === ASSIGNMENTS & RATINGS ===
    ASSIGNMENTS = 'assignments',
    RATINGS = 'ratings',
}

const ICON_REGISTRY: Record<EIconCategory, JSX.Element> = {
    // === CORE ENTITY TYPES ===
    [EIconCategory.SKILLS]: <HandMetal size={20} />,
    [EIconCategory.COMPETENCES]: <CircleStar size={20} />,
    [EIconCategory.CERTIFICATES]: <FileBadge size={20} />,
    [EIconCategory.ROLES]: <ShieldUser size={20} />,

    // === MAIN NAVIGATION ===
    [EIconCategory.DASHBOARD]: <Home />,
    [EIconCategory.PROFILE]: <Person />,
    [EIconCategory.EMPLOYEES]: <Groups />,
    [EIconCategory.DEPARTMENTS]: <Business />,
    [EIconCategory.TEAMS]: <Group />,
    [EIconCategory.SETTINGS]: <Settings />,
    [EIconCategory.T4LO_TALKS]: <Chat />,
    [EIconCategory.DEVELOPMENT]: <TrendingUp size={20} />,

    // === OVERVIEW & LISTS ===
    [EIconCategory.OVERVIEW]: <LayoutDashboard size={20} />,
    [EIconCategory.LIST_OVERVIEW]: <ClipboardList size={20} />,
    [EIconCategory.GENERAL]: <Info />,
    [EIconCategory.INFO]: <Info />,

    // === PROFILE ACTIONS ===
    [EIconCategory.EDIT]: <UserPen size={20} />,
    [EIconCategory.SHARE]: <Share2 size={20} />,
    [EIconCategory.PROFILE_PREVIEW]: <Eye size={20} />,
    [EIconCategory.CV_ANALYSE]: <WandSparkles size={20} />,
    [EIconCategory.DEVELOP]: <TrendingUp size={20} />,
    [EIconCategory.DEVELOPMENT_GOALS]: <Target size={20} />,

    // === OPPORTUNITY ACTIONS ===
    [EIconCategory.EDIT_OPPORTUNITY]: <ClipboardPen size={20} />,

    // === PROFILE SECTIONS ===
    [EIconCategory.CONTACT]: <Mail size={20} />,
    [EIconCategory.PROJECTS]: <Briefcase size={20} />,
    [EIconCategory.SUPERPOWERS]: <Sparkles size={20} />,
    [EIconCategory.ABOUTME]: <Contact size={20} />,
    [EIconCategory.MISSIONS]: <Rocket size={20} />,
    [EIconCategory.AVAILABILITY]: <CalendarClock size={20} />,
    [EIconCategory.TAB_FORECAST]: <CalendarCog size={20} />,

    // === CONTACT INFORMATION ===
    [EIconCategory.PHONE]: <Smartphone size={20} />,
    [EIconCategory.LOCATION]: <MapPin size={20} />,
    [EIconCategory.GITHUB]: <Github size={20} />,
    [EIconCategory.SLACK]: <Slack size={20} />,
    [EIconCategory.MS_TEAMS]: <UsersRound size={20} />,
    [EIconCategory.LINKEDIN]: <Linkedin size={20} />,
    [EIconCategory.WEBSITE]: <Globe size={20} />,

    // === OPPORTUNITY FIELDS ===
    [EIconCategory.EXTERNAL_ID]: <ExternalLink size={16} />,
    [EIconCategory.COMPANY]: <Building2 size={16} />,
    [EIconCategory.CUSTOMERS]: <Building2 size={16} />,
    [EIconCategory.UTILIZATION]: <Percent size={16} />,
    [EIconCategory.REMOTE]: <Wifi size={16} />,
    [EIconCategory.MONEY]: <DollarSign size={16} />,
    [EIconCategory.START_DATE]: <Flag size={16} />,
    [EIconCategory.END_DATE]: <CalendarCheck size={16} />,
    [EIconCategory.USERS]: <Users size={16} />,
    [EIconCategory.ATTACHMENTS]: <Files size={16} />,

    // === DATE FIELDS ===
    [EIconCategory.PUBLISHED_DATE]: <Calendar1 size={16} />,
    [EIconCategory.PROJECT_END]: <Goal size={16} />,
    [EIconCategory.DEADLINE]: <CalendarClock size={16} />,

    // === LEVEL INDICATOR ===
    [EIconCategory.LEVEL]: <Gauge size={20} />,
    [EIconCategory.LEVELCHANGE]: <Settings2 size={20} />,

    // === CATEGORIES ===
    [EIconCategory.CATEGORIES]: <Layers2 size={20} />,
    [EIconCategory.CATALOG]: <SquareLibrary size={20} />,

    // === MESSAGING & COMMUNICATION ===
    [EIconCategory.MESSAGES]: <Mail size={20} />,
    [EIconCategory.CALENDAR]: <CalendarClock size={20} />,

    // === ASSIGNMENTS & RATINGS ===
    [EIconCategory.ASSIGNMENTS]: <ClipboardList size={20} />,
    [EIconCategory.RATINGS]: <CircleStar size={20} />,
};

/**
 * Zentrale Icon-Registry für konsistente Icon-Verwendung in der gesamten App.
 *
 * @param category - Die Icon-Kategorie aus EIconCategory
 * @returns JSX.Element - Das entsprechende Icon oder Fallback
 *
 * @example
 * // Entity Types - überall gleich
 * <Button startIcon={getAppIcon(EIconCategory.SKILLS)}>Skills</Button>
 * <Tab icon={getAppIcon(EIconCategory.CERTIFICATES)} label="Zertifikate" />
 *
 * // Navigation
 * <MenuItem icon={getAppIcon(EIconCategory.DASHBOARD)}>Dashboard</MenuItem>
 *
 * // Actions
 * <IconButton>{getAppIcon(EIconCategory.EDIT)}</IconButton>
 * <Button startIcon={getAppIcon(EIconCategory.SHARE)}>Teilen</Button>
 */
export function getAppIcon(category: EIconCategory): JSX.Element {
    return ICON_REGISTRY[category] || <HelpOutline />;
}

/**
 * Hilfsfunktion: Konvertiert Entity-Typen zu Icon-Kategorien
 * Hilfsfunktion: Konvertiert Entity-Typen zu Icon-Kategorien
 */
export function entityTypeToIconCategory(entityType: string): EIconCategory | null {
    const typeMap: Record<string, EIconCategory> = {
        skill: EIconCategory.SKILLS,
        certificate: EIconCategory.CERTIFICATES,
        role: EIconCategory.ROLES,
        competence: EIconCategory.COMPETENCES,
        employee: EIconCategory.EMPLOYEES,
    };

    return typeMap[entityType.toLowerCase()] || null;
}
